import React, { useRef, useState, useEffect, Fragment } from 'react'
import debounce from 'lodash-es/debounce'
import Modal from 'react-bootstrap/Modal'
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch'
import Paginate from 'Shared/pagination'
import SearchInput from 'Documents/publish/components/search/searchInput'
import SearchResults from 'Documents/publish/components/search/searchResults'

function usePrevious(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const DEFAULT_LIMIT = 10

const Search = ({ versionId, themeColor, setShowSearchModal }) => {
  const [query, setQuery] = useState(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const [data, setData] = useState(null)
  const [hideSearchResults, setHideSearchResults] = useState(true)
  const [activePage, setActivePage] = useState(1)
  const previousQuery = usePrevious(query)
  const previousActivePage = usePrevious(activePage)
  const abortController = React.useRef()

  const setSearchQuery = query => {
    setQuery(query)
    if (!query || !query.length) {
      setHideSearchResults(true)
    } else {
      setHideSearchResults(false)
    }
  }

  const PublicDocumentSearchQuery = `query PublicDocumentSearchQuery($query: String!, $from: Int!, $limit: Int!, $versionId: ID!) {
    publicDocumentSearch(from: $from, query: $query, limit: $limit, versionId: $versionId) {
      totalResults
      hits
    }
  }`

  const debouncedQuery = React.useRef(
    debounce(args => {
      const controller = new window.AbortController()
      abortController.current = controller

      fetch('/graphql', {
        signal: controller.signal,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          query: args.query,
          operationName: args.operationName,
          variables: args.variables
        })
      })
        .then(r => r.json())
        .then(result => {
          setData(result.data)
          setSearchLoading(false)
        })
        .catch(e => {
          setData(null)
          setSearchLoading(false)
        })
    }, 500)
  )

  const abortLatest = () => abortController.current && abortController.current.abort()

  useEffect(() => {
    if (previousQuery !== query || previousActivePage !== activePage) {
      abortLatest()
      if (!query || !query.length) {
        setData(null)
        setSearchLoading(false)
        return
      }
      setSearchLoading(true)
      debouncedQuery.current({
        query: PublicDocumentSearchQuery,
        operationName: 'PublicDocumentSearchQuery',
        variables: {
          query: query || '',
          versionId: versionId || null,
          from: (activePage - 1) * DEFAULT_LIMIT,
          limit: DEFAULT_LIMIT
        }
      })
      if (previousQuery !== query) setActivePage(1)
    }
  }, [activePage, previousActivePage, previousQuery, query, PublicDocumentSearchQuery, versionId])

  const buildTotalPages = (totalResults, resultsPerPage) => {
    if (!resultsPerPage) return 0
    if (totalResults === 0) return 1
    return Math.ceil(totalResults / resultsPerPage)
  }

  const totalResults = data && data.publicDocumentSearch && data.publicDocumentSearch.totalResults
  const totalPages = buildTotalPages(totalResults, DEFAULT_LIMIT)
  const { publicDocumentSearch } = data || {}
  const { hits } = publicDocumentSearch || {}

  return (
    <>
      <Modal.Header>
        <SearchInput
          query={query}
          setSearchQuery={setSearchQuery}
          searchLoading={searchLoading}
          themeColor={themeColor}
          hideSearchResults={hideSearchResults}
          setHideSearchResults={setHideSearchResults}
        />
      </Modal.Header>
      <Modal.Body className="pt-0">
        <SearchResults
          query={query}
          hits={hits?.hits}
          totalResults={totalResults}
          totalPages={totalPages}
          activePage={activePage}
          setActivePage={setActivePage}
          themeColor={themeColor}
          setShowSearchModal={setShowSearchModal}
        />
      </Modal.Body>
      {totalPages > 1 && (
        <Modal.Footer className="justify-content-center">
          <Paginate totalPages={totalPages} activePage={activePage} setActivePage={setActivePage} themeColor={themeColor} />
        </Modal.Footer>
      )}
    </>
  )
}

export default Search
